import React, { useCallback, useRef, useState } from 'react';
import { Button, Col, Input, Row } from 'reactstrap';
import dayjs from 'dayjs';
import { ApiGroupRequest } from '@ibe/api';
import fallback from '../../../_Translations/generated/group-request.en.t.json';
import Keys from '../../../_Translations/generated/group-request.en.t.json.keys';
import useTranslation from '../../Hooks/useTranslation';
import DateRangeInput from '../Input/DateRangeInput/DateRangeInput';
import GroupsUtil from '../Util/GroupsUtil';
import useConfig from '../../Hooks/useConfig';
import Error from '../UI/Messages/Error';
import Props, {
  ADD_DAYS_TO_END,
  ADD_DAYS_TO_START,
  MIN_NUMBER_OF_SUPPORTED_PARTICIPANTS
} from './externals';
import TravelersPicker from './TravelerPicker';

const GroupRequest = (props: Props): JSX.Element => {
  const { t } = useTranslation('group-request', fallback, Keys);

  const { onSendAction, onCancelAction, maxDateRangeSpan, startingDate, endingDate, id } = props;

  const config = useConfig();
  const format = config.formatDate;

  const beginning = startingDate || dayjs().add(ADD_DAYS_TO_START, 'week').format(format);
  const ending = endingDate || dayjs().add(ADD_DAYS_TO_END, 'week').format(format);
  const [startDate, setStartDate] = useState<string>(beginning);
  const [endDate, setEndDate] = useState<string>(ending);

  const [numberOfAdults, setNumberOfAdults] = useState<number>(1);
  const [tempNumberOfAdults, setTempNumberOfAdults] = useState<number>(1);
  const [tempNumberOfChildren, setTempNumberOfChildren] = useState<number>(0);
  const [numberOfChildren, setNumberOfChildren] = useState<number>(0);
  const [freeText, setFreeText] = useState<string>('');
  const portalRef = useRef<HTMLDivElement>(null);

  const [notEnoughParticipantsError, setNotEnoughParticipantsError] = useState<boolean>(false);

  const handleDateRangeChange = useCallback(
    (start?: string, end?: string): void => {
      if (start) {
        setStartDate(dayjs(start).format(format));
      }
      if (end) {
        setEndDate(dayjs(end).format(format));
      }
    },
    [format]
  );

  const buildRequest = useCallback((): ApiGroupRequest => {
    return GroupsUtil.buildGroupRequest(
      startDate,
      endDate,
      freeText,
      numberOfAdults,
      numberOfChildren
    );
  }, [startDate, endDate, freeText, numberOfAdults, numberOfChildren]);

  const handleClick = useCallback((): void => {
    if (!notEnoughParticipantsError) {
      onSendAction(buildRequest());
    }
  }, [buildRequest, notEnoughParticipantsError, onSendAction]);

  const minDay = new Date();
  minDay.setDate(minDay.getDate() + config.datePickerMinDateOffset);

  return (
    <div className="iso__group_request" ref={portalRef}>
      <Row>
        <Col>
          <div className="font-weight-bold">{t(Keys.mainLabel)}</div>
          <div className="mt-2">{t(Keys.travelDetailsInfo)}</div>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col xs="12" sm="6">
          <div>{t(Keys.field.selectTravelDates)}</div>
          <DateRangeInput
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateRangeChange}
            minDate={minDay}
            maxDateRangeSpan={maxDateRangeSpan}
            dataTestId=""
            id="groupRequestRangeDate"
          />
        </Col>
        <Col xs="12" sm="6" className="mt-2 mt-sm-0">
          <div>{t(Keys.field.travelers)}</div>
          <TravelersPicker
            numberOfAdults={numberOfAdults}
            numberOfChildren={numberOfChildren}
            id={id}
            tempNumberOfAdults={tempNumberOfAdults}
            setTempNumberOfAdults={setTempNumberOfAdults}
            portalRef={portalRef}
            tempNumberOfChildren={tempNumberOfChildren}
            setTempNumberOfChildren={setTempNumberOfChildren}
            setNotEnoughParticipantsError={setNotEnoughParticipantsError}
            setNumberOfAdults={setNumberOfAdults}
            setNumberOfChildren={setNumberOfChildren}
          />
        </Col>
      </Row>
      <Row className="mt-4">
        <Col>
          <div>{t(Keys.freeTextInfo)}</div>
          <Input
            value={freeText}
            onChange={(evt): void => setFreeText(evt.target.value)}
            type="textarea"
            rows="5"
          />
        </Col>
      </Row>
      {notEnoughParticipantsError && (
        <Row className="mt-2">
          <Col className="mb-0">
            <Error message={t(Keys.messages.notEnoughParticipants)} />
          </Col>
        </Row>
      )}
      <Row className="mt-2 justify-content-end">
        <Col className="col-auto">
          <Button outline color="primary" onClick={onCancelAction}>
            <div className="p-1">{t(Keys.button.close)}</div>
          </Button>
          <Button
            className="ml-2"
            color="primary"
            disabled={numberOfAdults + numberOfChildren < MIN_NUMBER_OF_SUPPORTED_PARTICIPANTS}
            onClick={handleClick}
          >
            <div className="p-1">{t(Keys.button.send)}</div>
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default GroupRequest;
